import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { categories, getMoviesByCategory } from "@/mocks/movies";
import { articles } from "@/mocks/articles";
import type { MovieItem } from "@/mocks/movies";

const categoryLabels: Record<string, string> = {
  all: "全部",
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
  anime: "动漫",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || "movie");
  const [movies, setMovies] = useState<MovieItem[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  useEffect(() => {
    setMovies(getMoviesByCategory(activeTab));
  }, [activeTab]);

  useEffect(() => {
    const label = categoryLabels[activeTab] || "分类";
    document.title = `${label} | 爱看影院 - 免费${label}在线观看`;
  }, [activeTab]);

  const getArticleForMovie = useCallback((index: number) => {
    return articles[index % articles.length];
  }, []);

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://aikan.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: categoryLabels[activeTab] || "分类",
              },
            ],
          }),
        }}
      />

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
            首页
          </Link>
          <span>/</span>
          <span className="text-foreground-300">{categoryLabels[activeTab] || "分类"}</span>
        </nav>

        <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-50 mb-6">
          {categoryLabels[activeTab] || "分类"}
          <span className="text-base font-normal text-foreground-500 ml-3">
            共 {movies.length} 部
          </span>
        </h1>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          <div className="flex items-center bg-background-900/60 rounded-full p-1 border border-background-800/50">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === cat.slug
                    ? "bg-primary-500 text-background-950"
                    : "text-foreground-400 hover:text-foreground-200"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {movies.map((movie, index) => {
            const article = getArticleForMovie(index);
            return (
              <Link
                key={movie.id}
                to={`/article/${article.id}`}
                className="group relative flex flex-col rounded-xl overflow-hidden bg-background-900/60 border border-background-800/50 hover:border-primary-500/30 hover:bg-background-800/60 transition-all duration-300 cursor-pointer"
              >
                <div className="relative aspect-[2/3] overflow-hidden">
                  <img
                    src={movie.coverImage}
                    alt={movie.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-950/70 text-xs text-accent-400">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-[10px]"></i>
                    </div>
                    {movie.rating}
                  </div>
                  {movie.isNew && (
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500/30 text-accent-300">
                        NEW
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
                    {movie.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {movie.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-1.5 py-0.5 rounded text-[10px] bg-background-800/50 text-foreground-500"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-foreground-500 mt-1.5">
                    {movie.year} / {movie.region}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}